// Utilidad para verificar permisos en el frontend
import { useAuth } from '../context/AuthContext';

/**
 * Hook personalizado para verificar permisos específicos
 */
export const usePermissions = () => {
  const { 
    isGlobalAdmin, 
    isCompanyAdmin, 
    canWrite, 
    isViewer,
    currentTenant,
    user 
  } = useAuth();

  // Obtener el rol en la empresa actual
  const getCurrentRole = () => {
    if (isGlobalAdmin) return 'global_admin';
    if (!currentTenant || !user?.companies) return null;
    
    const currentCompany = user.companies.find(c => c.id === currentTenant);
    return currentCompany?.role || null;
  };

  return {
    // Roles básicos
    isGlobalAdmin,
    isCompanyAdmin,
    canWrite,
    isViewer,
    currentRole: getCurrentRole(),

    // Permisos específicos
    can: {
      // Empresas
      viewAllCompanies: isGlobalAdmin,
      createCompany: isGlobalAdmin,
      editCompany: isGlobalAdmin,
      deleteCompany: isGlobalAdmin,

      // Usuarios
      viewAllUsers: isGlobalAdmin,
      viewCompanyUsers: isGlobalAdmin || isCompanyAdmin,
      createUser: isGlobalAdmin || isCompanyAdmin,
      editUser: isGlobalAdmin || isCompanyAdmin,
      deleteUser: isGlobalAdmin,
      changeUserRole: isGlobalAdmin,

      // Productos
      viewProducts: true, // Todos pueden ver
      createProduct: isGlobalAdmin || isCompanyAdmin,
      editProduct: isGlobalAdmin || isCompanyAdmin,
      deleteProduct: isGlobalAdmin || isCompanyAdmin,
      reorderProducts: isGlobalAdmin || isCompanyAdmin,

      // Categorías
      viewCategories: true, // Todos pueden ver
      createCategory: isGlobalAdmin || isCompanyAdmin,
      editCategory: isGlobalAdmin || isCompanyAdmin,
      deleteCategory: isGlobalAdmin || isCompanyAdmin,
      reorderCategories: isGlobalAdmin || isCompanyAdmin,

      // Tarifas
      viewRates: true, // Todos pueden ver
      generatePDF: canWrite, // Admin y agent pueden generar
      downloadPDF: canWrite,

      // Estadísticas
      viewAllStatistics: isGlobalAdmin,
      viewCompanyStatistics: isGlobalAdmin || isCompanyAdmin,
      exportStatistics: isGlobalAdmin || isCompanyAdmin,

      // Configuración
      viewSettings: isGlobalAdmin || isCompanyAdmin,
      editGlobalSettings: isGlobalAdmin,
      editCompanySettings: isGlobalAdmin || isCompanyAdmin,

      // Historial
      viewAllAccessHistory: isGlobalAdmin,
      viewCompanyAccessHistory: isGlobalAdmin || isCompanyAdmin,
      viewOwnAccessHistory: true, // Todos pueden ver su propio historial
    }
  };
};

/**
 * Obtener descripción legible del rol
 */
export const getRoleLabel = (role) => {
  const roles = {
    'global_admin': 'Administrador Global',
    'company_admin': 'Administrador de Empresa (Heredado)',
    'agent': 'Agente',
    'admin': 'Administrador',
    'viewer': 'Solo Lectura'
  };
  return roles[role] || role;
};

/**
 * Obtener color del rol para badges
 */
export const getRoleColor = (role) => {
  const colors = {
    'global_admin': 'bg-purple-100 text-purple-800 border-purple-300',
    'company_admin': 'bg-blue-100 text-blue-800 border-blue-300',
    'admin': 'bg-blue-100 text-blue-800 border-blue-300',
    'agent': 'bg-green-100 text-green-800 border-green-300',
    'viewer': 'bg-gray-100 text-gray-800 border-gray-300'
  };
  return colors[role] || 'bg-gray-100 text-gray-800 border-gray-300';
};

/**
 * Obtener icono del rol
 */
export const getRoleIcon = (role) => {
  const icons = {
    'global_admin': '👑',
    'company_admin': '🔧',
    'admin': '⚙️',
    'agent': '👤',
    'viewer': '👁️'
  };
  return icons[role] || '📋';
};

/**
 * Verificar si el usuario puede realizar una acción sobre otro usuario
 */
export const canManageUser = (currentUser, targetUser) => {
  // Global admin puede gestionar a todos
  if (currentUser.role === 'global_admin') return true;

  // Company admin puede gestionar usuarios de sus empresas
  if (!currentUser.companies || !targetUser.companies) return false;

  const currentUserAdminCompanies = currentUser.companies
    .filter(c => c.role === 'admin')
    .map(c => c.id);

  const targetUserCompanies = targetUser.companies.map(c => c.id);

  // Verificar si hay al menos una empresa en común donde el usuario actual es admin
  return targetUserCompanies.some(companyId => 
    currentUserAdminCompanies.includes(companyId)
  );
};

/**
 * Obtener descripción de permisos para mostrar al usuario
 */
export const getPermissionDescription = (role) => {
  const descriptions = {
    'global_admin': 'Acceso completo al sistema. Puede gestionar empresas, usuarios y todas las configuraciones.',
    'company_admin': 'Rol heredado. Se recomienda usar el rol de empresa específico.',
    'admin': 'Administrador de empresa. Puede gestionar productos, categorías y usuarios de su empresa.',
    'agent': 'Agente/Vendedor. Puede ver tarifas y generar cotizaciones. No puede modificar productos.',
    'viewer': 'Solo lectura. Puede ver tarifas pero no puede generar PDFs ni modificar datos.'
  };
  return descriptions[role] || 'Sin descripción disponible.';
};

export default usePermissions;
